/**
 * Token Management Utilities
 * Centralized token fetching, validation, and error handling
 */

import { getGoogleTokensFromSupabase, getMsftTokensFromSupabase } from '../supabase.js';

// ============================================================================
// TYPES
// ============================================================================

export interface TokenData {
  access_token: string;
  refresh_token?: string;
  access_token2?: string;
  refresh_token2?: string;
}

export interface TokenCheckResult {
  hasGoogle: boolean;
  hasMicrosoft: boolean;
  googleTokens?: TokenData;
  microsoftTokens?: TokenData;
}

// ============================================================================
// TOKEN RETRIEVAL
// ============================================================================

/**
 * Get tokens for a specific email with fallback logic
 */
export async function getTokensForEmail(
  email: string,
  provider: 'google' | 'microsoft' | 'both' = 'both'
): Promise<TokenCheckResult> {
  const result: TokenCheckResult = {
    hasGoogle: false,
    hasMicrosoft: false,
  };

  if (provider === 'google' || provider === 'both') {
    const googleTokens = await getGoogleTokensFromSupabase(email);
    result.hasGoogle = !!googleTokens?.access_token;
    if (googleTokens) {
      result.googleTokens = googleTokens;
    }
  }

  if (provider === 'microsoft' || provider === 'both') {
    const microsoftTokens = await getMsftTokensFromSupabase(email);
    result.hasMicrosoft = !!microsoftTokens?.access_token;
    if (microsoftTokens) {
      result.microsoftTokens = microsoftTokens;
    }
  }

  return result;
}

/**
 * Get Google tokens for an email
 */
export async function getGoogleTokens(email: string): Promise<TokenData | null> {
  return await getGoogleTokensFromSupabase(email);
}

/**
 * Get Microsoft tokens for an email
 */
export async function getMicrosoftTokens(email: string): Promise<TokenData | null> {
  return await getMsftTokensFromSupabase(email);
}

// ============================================================================
// TOKEN VALIDATION
// ============================================================================

/**
 * Validate that a token exists and is not empty
 */
export function validateTokenExists(token: TokenData | null | undefined, provider: string): boolean {
  return !!(token?.access_token);
}

/**
 * Validate that at least one token exists for an email
 */
export async function validateEmailHasTokens(email: string): Promise<boolean> {
  const result = await getTokensForEmail(email);
  return result.hasGoogle || result.hasMicrosoft;
}

/**
 * Validate that a specific provider token exists
 */
export async function validateProviderToken(
  email: string,
  provider: 'google' | 'microsoft'
): Promise<boolean> {
  const result = await getTokensForEmail(email, provider);
  return provider === 'google' ? result.hasGoogle : result.hasMicrosoft;
}

/**
 * Check which providers are connected for an email
 */
export async function getConnectedProviders(email: string): Promise<string[]> {
  const result = await getTokensForEmail(email);
  const providers: string[] = [];

  if (result.hasGoogle) providers.push('google');
  if (result.hasMicrosoft) providers.push('microsoft');

  return providers;
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Create a standardized token error
 */
export function createTokenError(
  email: string,
  provider: string,
  message?: string
): Error & { code?: string; statusCode?: number } {
  const error = new Error(
    message || `No valid access token found for email: ${email}`
  ) as Error & { code?: string; statusCode?: number };
  error.code = 'TOKEN_NOT_FOUND';
  error.statusCode = 422;
  return error;
}

/**
 * Create a standardized calendar not connected error
 */
export function createCalendarNotConnectedError(
  disconnectedEmails: string[]
): Error & { code?: string; statusCode?: number; details?: any } {
  const error = new Error(
    disconnectedEmails.length === 1
      ? `Calendar is not connected for ${disconnectedEmails[0]}`
      : `Calendars are not connected for: ${disconnectedEmails.join(', ')}`
  ) as Error & { code?: string; statusCode?: number; details?: any };
  error.code = 'CALENDAR_NOT_CONNECTED';
  error.statusCode = 422;
  error.details = {
    emails: disconnectedEmails,
    message: `No calendar connection found for ${disconnectedEmails.length === 1 ? 'this email' : 'these emails'}. Please connect a Google or Microsoft calendar account.`,
    provider: null,
  };
  return error;
}

// ============================================================================
// BATCH TOKEN OPERATIONS
// ============================================================================

/**
 * Check tokens for multiple emails and separate connected from disconnected
 */
export async function checkEmailsTokenStatus(
  emails: string[]
): Promise<{ connected: string[]; disconnected: string[] }> {
  const connected: string[] = [];
  const disconnected: string[] = [];

  for (const email of emails) {
    const hasTokens = await validateEmailHasTokens(email);
    if (hasTokens) {
      connected.push(email);
    } else {
      disconnected.push(email);
    }
  }

  return { connected, disconnected };
}

/**
 * Filter emails to only those with valid tokens
 */
export async function filterEmailsByTokenStatus(emails: string[]): Promise<string[]> {
  const { connected } = await checkEmailsTokenStatus(emails);
  return connected;
}

/**
 * Check if service account is available as fallback
 */
export function hasServiceAccountFallback(): boolean {
  if (typeof process === 'undefined' || !process.env) {
    return false;
  }

  return !!(
    process.env.GOOGLE_SERVICE_ACCOUNT_EMAIL &&
    process.env.GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY
  );
}
